import { useState, useEffect } from 'react';
import api from '../services/api';
import { X, Printer, TrendingUp, DollarSign, RotateCcw, CreditCard, Banknote, AlertCircle, Calendar } from 'lucide-react';

export default function EodSummary({ onClose, currencySymbol = '$' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    setLoading(true);
    api.get(`/reports/eod?date=${selectedDate}`)
      .then(res => {
        setData(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [selectedDate]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-[100] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
        <p className="text-white/60 font-black uppercase tracking-widest text-xs">Generating Report...</p>
      </div>
    </div>
  );

  if (!data) return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-[100] flex items-center justify-center">
      <div className="bg-theme-card p-12 rounded-3xl border border-theme text-center space-y-4">
        <AlertCircle size={48} className="text-rose-500 mx-auto" />
        <h3 className="text-xl font-black text-theme-main">Failed to load report</h3>
        <button onClick={onClose} className="px-8 py-2 bg-brand text-white rounded-xl font-bold">Close</button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-theme-card w-full max-w-2xl rounded-3xl shadow-2xl border border-theme overflow-hidden flex flex-col max-h-[90vh] print:max-h-none print:shadow-none print:border-none print:rounded-none">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-theme bg-theme-main/10 flex justify-between items-center print:hidden">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand/10 text-brand rounded-xl"><Calendar size={20} /></div>
            <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">End of Day Reconciliation</h3>
          </div>
          <div className="flex items-center gap-3">
            <input 
              type="date" 
              value={selectedDate} 
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-theme-main border border-theme rounded-xl px-3 py-1.5 text-xs font-bold text-theme-main outline-none focus:ring-1 focus:ring-brand"
            />
            <button onClick={onClose} className="text-theme-muted hover:text-theme-main transition-colors p-1"><X size={24} /></button>
          </div>
        </div>

        {/* Report Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar print:p-0">
          
          {/* Printable Header */}
          <div className="hidden print:block text-center mb-8 border-b-2 border-dashed border-gray-300 pb-6">
            <h1 className="text-2xl font-black uppercase">EOD RECONCILIATION REPORT</h1>
            <p className="text-sm text-gray-600 mt-2">Date: {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>

          {/* Primary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:grid-cols-2">
            <StatBox label="Gross Sales" value={data.grossSales} color="brand" currency={currencySymbol} />
            <StatBox label="Discounts" value={data.discounts} color="amber" currency={currencySymbol} />
            <StatBox label="Refunds" value={data.refunds} color="rose" currency={currencySymbol} />
            <StatBox label="Net Revenue" value={data.netSales} color="emerald" currency={currencySymbol} isMain={true} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
            {/* Payment Breakdown */}
            <div className="space-y-4">
              <h4 className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] flex items-center gap-2">
                <Banknote size={14} /> Payment Reconciliation
              </h4>
              <div className="bg-theme-main/10 rounded-2xl p-6 border border-theme space-y-4 print:bg-white print:border-gray-200">
                {Object.entries(data.payments).map(([method, amount]) => (
                  <div key={method} className="flex justify-between items-center group">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-theme-main rounded-lg text-theme-muted border border-theme group-hover:text-brand transition-colors">
                        {method === 'cash' ? <Banknote size={16} /> : method === 'card' ? <CreditCard size={16} /> : <TrendingUp size={16} />}
                      </div>
                      <span className="text-sm font-bold text-theme-main capitalize">{method}</span>
                    </div>
                    <span className="text-sm font-black text-theme-main">{currencySymbol}{amount.toFixed(2)}</span>
                  </div>
                ))}
                {Object.keys(data.payments).length === 0 && (
                  <p className="text-xs text-theme-muted italic text-center py-4">No payments recorded for this period.</p>
                )}
                <div className="pt-4 border-t border-theme border-dashed flex justify-between items-center print:border-gray-300">
                  <span className="text-sm font-black text-theme-main uppercase">Total Collected</span>
                  <span className="text-lg font-black text-brand">{currencySymbol}{Object.values(data.payments).reduce((a, b) => a + b, 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Other Metrics */}
            <div className="space-y-4">
              <h4 className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] flex items-center gap-2">
                <TrendingUp size={14} /> Operations Summary
              </h4>
              <div className="grid grid-cols-1 gap-3">
                <MetricRow label="Transactions" value={data.transactions} icon={RotateCcw} />
                <MetricRow label="Returns Processed" value={data.refundCount} icon={AlertCircle} color="rose" />
                <MetricRow label="Tax Collected" value={`${currencySymbol}${data.tax.toFixed(2)}`} icon={DollarSign} />
              </div>
            </div>
          </div>

          {/* Signature Line for Print */}
          <div className="hidden print:block mt-20 pt-8 border-t border-gray-300">
            <div className="flex justify-between">
              <div className="w-48 text-center">
                <div className="border-b border-gray-400 mb-2 h-8"></div>
                <p className="text-[10px] font-bold uppercase">Store Manager Signature</p>
              </div>
              <div className="w-48 text-center">
                <div className="border-b border-gray-400 mb-2 h-8"></div>
                <p className="text-[10px] font-bold uppercase">Auditor Signature</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 bg-theme-main/10 border-t border-theme flex justify-end gap-3 print:hidden">
          <button 
            onClick={onClose}
            className="px-6 py-2.5 bg-theme-card border border-theme text-theme-main rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-theme-main transition-all"
          >
            Close
          </button>
          <button 
            onClick={handlePrint}
            className="bg-brand hover:bg-brand-light text-white px-8 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-brand/20 transition-all"
          >
            <Printer size={18} /> Print EOD Report
          </button>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, color, currency, isMain }) {
  const colors = {
    brand: 'bg-brand/10 text-brand border-brand/20',
    amber: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    rose: 'bg-rose-500/10 text-rose-500 border-rose-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
  };

  return (
    <div className={`p-5 rounded-2xl border ${colors[color]} ${isMain ? 'ring-2 ring-emerald-500 ring-offset-2 ring-offset-theme-card' : ''}`}>
      <p className="text-[9px] font-black uppercase tracking-[0.15em] opacity-80 mb-1">{label}</p>
      <p className="text-xl font-black truncate">{currency}{value.toFixed(2)}</p>
    </div>
  );
}

function MetricRow({ label, value, icon: Icon, color = "brand" }) {
  return (
    <div className="bg-theme-main/10 p-4 rounded-xl border border-theme flex items-center justify-between group hover:border-brand transition-all">
      <div className="flex items-center gap-3">
        <div className={`p-1.5 rounded-lg text-${color}-500 opacity-60 group-hover:opacity-100 transition-opacity`}>
          <Icon size={18} />
        </div>
        <span className="text-sm font-bold text-theme-main">{label}</span>
      </div>
      <span className="text-lg font-black text-theme-main">{value}</span>
    </div>
  );
}
